import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import type { AppDispatch, RootState } from '../store/store';
import { apiUrl } from '../config/api';

export type DayStatus =
  | 'filled'
  | 'missing'
  | 'off'
  | 'holiday'
  | 'vacation'
  | 'sick'
  | 'future';

export interface DayEntry {
  _id: string;
  startTime: string;
  endTime?: string;
  type: string;
  pausedDuration: number;
  breakMinutes?: number;
}

export interface DayOverview {
  date: string;
  weekday: string;
  soll: number;
  ist: number;
  status: DayStatus;
  planned: boolean;
  holidayName: string | null;
  entries: DayEntry[];
}

export interface MonthlyOverview {
  month: string;
  userId: string;
  days: DayOverview[];
  totals: { soll: number; ist: number; overtime: number; undertime: number };
}

export interface UserMissingRow {
  userId: string;
  username: string;
  email: string;
  plannedDaysCount: number;
  hasPlanning: boolean;
}

export interface ReminderResultRow {
  user: string;
  recipient: string | null;
  sent: boolean;
  dryRun?: boolean;
  error?: string;
}

export interface ReminderRunResult {
  dryRun: boolean;
  testEmail: string | null;
  processed: number;
  results: ReminderResultRow[];
}

export interface TeamPresencePerson {
  userId: string;
  username: string;
  dates: string[];
}
export interface TeamPresence {
  from: string;
  to: string;
  people: TeamPresencePerson[];
}

interface OverviewState {
  data: MonthlyOverview | null;
  monthsData: Record<string, MonthlyOverview>;
  loading: boolean;
  error?: string;
  adminRows: UserMissingRow[];
  adminLoading: boolean;
  adminError?: string;
  reminderResult: ReminderRunResult | null;
  reminderSending: boolean;
  reminderError?: string;
  team: TeamPresence | null;
  teamLoading: boolean;
  teamError?: string;
}

const initialState: OverviewState = {
  data: null,
  monthsData: {},
  loading: false,
  error: undefined,
  adminRows: [],
  adminLoading: false,
  adminError: undefined,
  reminderResult: null,
  reminderSending: false,
  reminderError: undefined,
  team: null,
  teamLoading: false,
  teamError: undefined,
};

const authHeaders = (token?: string | null) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const handleAuth = (res: Response, dispatch: AppDispatch) => {
  if (res.status === 401) {
    dispatch(logout());
    throw new Error('Token abgelaufen, bitte neu anmelden.');
  }
};

// 📅 Monatsübersicht laden
export const fetchMonthlyOverview = createAsyncThunk<
  MonthlyOverview,
  { month: string; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/fetch', async ({ month, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const url = new URL(apiUrl('/api/time-entries/monthly-overview'));
  url.searchParams.append('month', month);
  if (userId) url.searchParams.append('userId', userId);

  const res = await fetch(url.toString(), { headers: authHeaders(token) });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Fehler beim Laden der Monatsübersicht');
  }
  return res.json();
});

// 👥 Admin: Mitarbeiter ohne Monatsplanung
export const fetchMissingOverview = createAsyncThunk<
  UserMissingRow[],
  void,
  { state: RootState; dispatch: AppDispatch }
>('overview/missingOverview', async (_arg, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entries/missing-overview'), {
    headers: authHeaders(token),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Fehler beim Laden der Admin-Übersicht');
  }
  return res.json();
});

// ✉️ Admin: Erinnerungs-Mails auslösen
export const sendReminders = createAsyncThunk<
  ReminderRunResult,
  { dryRun?: boolean } | void,
  { state: RootState; dispatch: AppDispatch }
>('overview/sendReminders', async (arg, { getState, dispatch }) => {
  const token = getState().auth.token;
  const body =
    arg && typeof arg.dryRun === 'boolean' ? JSON.stringify({ dryRun: arg.dryRun }) : '{}';
  const res = await fetch(apiUrl('/api/time-entries/send-reminders'), {
    method: 'POST',
    headers: authHeaders(token),
    body,
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.message || 'Fehler beim Senden der Erinnerungen');
  }
  return res.json();
});

// ✅ "Reprendre le prévu"
export const fillDay = createAsyncThunk<
  void,
  { date: string; month: string; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/fillDay', async ({ date, month, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entries/fill-day'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ date, userId }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Übernehmen fehlgeschlagen');
  }
  await dispatch(fetchMonthlyOverview({ month, userId }));
});

// ➕ Manueller Eintrag
export const createManualEntry = createAsyncThunk<
  void,
  {
    startTime: string;
    endTime: string;
    pausedDuration: number;
    month: string;
    userId?: string;
  },
  { state: RootState; dispatch: AppDispatch }
>('overview/createManual', async (payload, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/time-entries'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({
      userId: payload.userId,
      startTime: payload.startTime,
      endTime: payload.endTime,
      pausedDuration: payload.pausedDuration,
    }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Eintrag konnte nicht erstellt werden');
  }
  await dispatch(fetchMonthlyOverview({ month: payload.month, userId: payload.userId }));
});

// ✏️ Eintrag bearbeiten
export const editDayEntry = createAsyncThunk<
  void,
  {
    id: string;
    startTime: string;
    endTime: string;
    pausedDuration: number;
    month: string;
    userId?: string;
  },
  { state: RootState; dispatch: AppDispatch }
>('overview/edit', async (payload, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/time-entries/${payload.id}`), {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({
      startTime: payload.startTime,
      endTime: payload.endTime,
      pausedDuration: payload.pausedDuration,
    }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Eintrag konnte nicht bearbeitet werden');
  }
  await dispatch(fetchMonthlyOverview({ month: payload.month, userId: payload.userId }));
});

// 🗑️ Eintrag löschen
export const deleteDayEntry = createAsyncThunk<
  void,
  { id: string; month: string; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/delete', async ({ id, month, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl(`/api/time-entries/${id}`), {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  handleAuth(res, dispatch);
  if (!res.ok && res.status !== 204) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Eintrag konnte nicht gelöscht werden');
  }
  await dispatch(fetchMonthlyOverview({ month, userId }));
});

// 🗓️ Mehrere Tage als geplant markieren (nur Mutation)
export const planDays = createAsyncThunk<
  void,
  { dates: string[]; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/planDays', async ({ dates, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/planned-days'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ dates, userId }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Planen fehlgeschlagen');
  }
});

// 🔁 Wiederkehrende Planung ueber eine Spanne (nur Mutation)
export const planRecurring = createAsyncThunk<
  { updated: number },
  { from: string; to: string; weekdays: number[]; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/planRecurring', async ({ from, to, weekdays, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/planned-days/recurring'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ from, to, weekdays, userId }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Wiederkehrende Planung fehlgeschlagen');
  }
  return res.json();
});

// 🗑️ Geplante Tage entfernen (nur Mutation)
export const unplanDays = createAsyncThunk<
  void,
  { dates: string[]; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/unplanDays', async ({ dates, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/planned-days'), {
    method: 'DELETE',
    headers: authHeaders(token),
    body: JSON.stringify({ dates, userId }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Entfernen fehlgeschlagen');
  }
});

// ✅ Mehrere Tage als gearbeitet erfassen (nur Mutation)
export const recordWorkedDays = createAsyncThunk<
  void,
  { dates: string[]; hours: number; userId?: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/recordWorked', async ({ dates, hours, userId }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const res = await fetch(apiUrl('/api/planned-days/record-worked'), {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ dates, hours, userId }),
  });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Erfassen fehlgeschlagen');
  }
});

// 👥 Team-Präsenz laden (alle Mitarbeiter, nur Arbeitstage)
export const fetchTeamPresence = createAsyncThunk<
  TeamPresence,
  { from: string; to: string },
  { state: RootState; dispatch: AppDispatch }
>('overview/team', async ({ from, to }, { getState, dispatch }) => {
  const token = getState().auth.token;
  const url = new URL(apiUrl('/api/planned-days/team'));
  url.searchParams.append('from', from);
  url.searchParams.append('to', to);
  const res = await fetch(url.toString(), { headers: authHeaders(token) });
  handleAuth(res, dispatch);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Fehler beim Laden der Team-Übersicht');
  }
  return res.json();
});

const overviewSlice = createSlice({
  name: 'overview',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonthlyOverview.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchMonthlyOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.monthsData[action.payload.month] = action.payload;
      })
      .addCase(fetchMonthlyOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchMissingOverview.pending, (state) => {
        state.adminLoading = true;
        state.adminError = undefined;
      })
      .addCase(fetchMissingOverview.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminRows = action.payload;
      })
      .addCase(fetchMissingOverview.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.error.message;
      })
      .addCase(sendReminders.pending, (state) => {
        state.reminderSending = true;
        state.reminderError = undefined;
        state.reminderResult = null;
      })
      .addCase(sendReminders.fulfilled, (state, action) => {
        state.reminderSending = false;
        state.reminderResult = action.payload;
      })
      .addCase(sendReminders.rejected, (state, action) => {
        state.reminderSending = false;
        state.reminderError = action.error.message;
      })
      .addCase(fetchTeamPresence.pending, (state) => {
        state.teamLoading = true;
        state.teamError = undefined;
      })
      .addCase(fetchTeamPresence.fulfilled, (state, action) => {
        state.teamLoading = false;
        state.team = action.payload;
      })
      .addCase(fetchTeamPresence.rejected, (state, action) => {
        state.teamLoading = false;
        state.teamError = action.error.message;
      });
  },
});

export default overviewSlice.reducer;
